import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CanvasPageRenderer from "../../Canvas/components/CanvasPageRenderer";

/**
 * Standalone route: canvas stage preview opened via window.open from Document view.
 * Payload is passed via sessionStorage key in ?key= (set right before opening the window).
 */
const CanvasStagePreviewPage = () => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");

  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const previewViewportRef = useRef(null);
  const previewCanvasContentRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [scaledCanvasHeight, setScaledCanvasHeight] = useState(null);

  useEffect(() => {
    if (!key) {
      setLoadError("Missing preview key.");
      return;
    }
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        setLoadError(
          "Preview data expired or missing. Close this window and open the preview again from the document.",
        );
        return;
      }
      const parsed = JSON.parse(raw);
      setPayload(parsed);
      if (parsed?.title && typeof document !== "undefined") {
        document.title = parsed.title;
      }
    } catch (e) {
      console.error("[CanvasStagePreviewPage]", e);
      setLoadError("Could not load preview data.");
    }
  }, [key]);

  useEffect(() => {
    if (!key) return undefined;
    const clearKey = () => {
      try {
        sessionStorage.removeItem(key);
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", clearKey);
    return () => {
      window.removeEventListener("pagehide", clearKey);
    };
  }, [key]);

  useLayoutEffect(() => {
    const bodyEl = document.body;
    const prevBodyMargin = bodyEl.style.margin;
    // Keep popup/window scrollbar aligned to the right edge.
    bodyEl.style.margin = "0";
    return () => {
      bodyEl.style.margin = prevBodyMargin;
    };
  }, []);

  useLayoutEffect(() => {
    if (!payload || payload.type !== "canvasData") {
      setPreviewScale(1);
      setScaledCanvasHeight(null);
      return;
    }
    // Keep default 1x zoom in popup; do not auto-fit/auto-zoom-out.
    setPreviewScale(1);
    setScaledCanvasHeight(null);
  }, [payload]);

  if (loadError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "#fff",
          color: "#333",
          fontFamily: "system-ui, sans-serif",
          textAlign: "center",
          maxWidth: 480,
          margin: "0 auto",
        }}
      >
        {loadError}
      </div>
    );
  }

  if (!payload) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div
      data-canvas-stage-preview-root="true"
      style={{
        minHeight: "100vh",
        boxSizing: "border-box",
        padding: 12,
        background: "#fff",
      }}
    >
      {payload.type === "canvasData" ? (
        <div
          ref={previewViewportRef}
          style={{
            minHeight: "calc(100vh - 24px)",
            overflow: "visible",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              minHeight: scaledCanvasHeight || "auto",
            }}
          >
            <div
              style={{
                transform: `scale(${previewScale})`,
                transformOrigin: "top center",
                willChange: "transform",
              }}
            >
              <div ref={previewCanvasContentRef}>
                <CanvasPageRenderer
                  canvasData={payload.canvasData}
                  width={Math.min(window.innerWidth * 0.92, 1300)}
                  showSnapshots={false}
                  skipLatestPartsRefresh={true}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "calc(100vh - 24px)",
          }}
        >
          <img
            src={payload.imageUrl}
            alt={payload.title || "Canvas snapshot"}
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "calc(100vh - 24px)",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CanvasStagePreviewPage;
